import {German} from 'flatpickr/dist/l10n/de';
import {Instance} from 'flatpickr/dist/types/instance';
import {useEffect, useState} from 'react';
import Flatpickr from 'react-flatpickr';
import {DatePickerChangeHandler} from '../../types/Handler';
import Input from '../input/Input';
import Paragraph from '../paragraph/Paragraph';
import {DfbCalenderSymbol} from '../symbol/Symbol';
import {ShortcutButtonsPlugin} from './FlatPickrCustomizer';

export type DatePickerProps = {
    id: string,
    label: string,
    todayLabel?: string,
    value?: string | Date,
    errorMessage?: string,
    required?: boolean,
    onSubmit?: DatePickerChangeHandler,
    hideTodayButton?: boolean,
    minDate?: string,
    maxDate?: string,
    /** should be used for widgets only */
    static?: boolean
};

export const getDatePickerString = (date) => {
    return `${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`;
};

const DatePicker = (props: DatePickerProps) => {
    const [value, setValue] = useState(props.value);
    const [dateStr, setDateStr] = useState<string>();

    useEffect(() => {
        setValue(props.value);

        let newDateStr: string;
        if (props.value) {
            newDateStr = typeof props.value === 'string' ? props.value : getDatePickerString(props.value);
        }
        setDateStr(newDateStr);
    }, [props.value]);

    const getOption = () => {
        const plugins = [];

        if (!props.hideTodayButton) {
            plugins.push(ShortcutButtonsPlugin({
                button: [
                    {
                        label: props.todayLabel ? props.todayLabel : 'Heute',
                        className: 'dfb-Datepicker-todayButton'
                    }
                ],
                onClick: (index, fp) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    fp.setDate(today, true);
                    fp.close();
                }
            }));
        }

        return {
            static: props.static,
            locale: German,
            dateFormat: 'd.m.Y',
            altFormat: 'm/d/Y',
            ariaDateFormat: 'm/d/Y',
            allowInput: true,
            wrap: true,
            disableMobile: true,
            minDate: props.minDate,
            maxDate: props.maxDate,
            plugins
        };
    };

    const updateSingleDataMode = (data?: string, dateObj?: Date) => {
        setValue(dateObj);
        setDateStr(data);
        if (props.onSubmit) {
            props.onSubmit(props.id, data, dateObj);
        }
    };

    return (
        <>
            <div className={`dfb-Datepicker`} data-mode="single">
                <Flatpickr
                    className="dfb-Datepicker flatpicker"
                    options={getOption()}
                    value={value ?? ''}
                    onChange={(selectedDates: Date[], dd: string, inst: Instance) => {
                        if (selectedDates.length > 0)
                            updateSingleDataMode(inst.formatDate(selectedDates[0], inst.config.dateFormat), selectedDates[0]);
                        else
                            updateSingleDataMode(undefined);
                    }}
                >
                    <div className="dfb-Datepicker-container">
                        <Input
                            value={dateStr ?? ''}
                            autoComplete="off"
                            className={`dfb-Datepicker-input`}
                            type="text"
                            id={`dfb-Input-datepicker-${props.id}`}
                            errorMessage={props.errorMessage}
                            hideErrorMessage={true}
                            label={props.label}
                            required={props.required}
                            dataInput={true}
                        />
                    </div>
                    <input className="dfb-Datepicker-hiddenValueInput" type="hidden" value={dateStr ?? ''}/>
                    <button className="dfb-Datepicker-toggle">
                        <div className="dfb-Icon dfb-Datepicker-toggleIcon" data-toggle={true}>
                            <DfbCalenderSymbol/>
                        </div>
                    </button>
                </Flatpickr>
                {
                    props.errorMessage &&
                    <Paragraph size="small" className="dfb-Input-errorMessage">
                        {props.errorMessage}
                    </Paragraph>
                }
            </div>
        </>
    );
};

DatePicker.defaulProps = {
    static: false
};

export default DatePicker;
